import moment from "moment";

// Form rules for Payroll > Pay Inputs > Loans & Advances. The records come
// from the advance request API (loansApi.js).
//
// A Loan is repaid through payroll from a start month: one month = Full
// repayment, 2 or more = monthly instalments. A Salary Advance has no
// repayment schedule (backend rule), so it takes no repayment fields.

export const MONTH_KEY = "YYYY-MM";
export const TEXT_MAX = 200;

export const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const currentMonth = () => moment().format(MONTH_KEY);
export const monthLabel = (key) => {
  const parsed = moment(key, MONTH_KEY, true);
  return parsed.isValid() ? parsed.format("MMM YYYY") : key || "–";
};
const addMonths = (key, count) => moment(key, MONTH_KEY).add(count, "month").format(MONTH_KEY);

// Months a repayment can start in: this month and the next 11. `extra`
// keeps a request's own (older) month selectable when editing.
export const monthOptions = (extra = []) => {
  const keys = Array.from({ length: 12 }, (_, index) => addMonths(currentMonth(), index));
  extra.filter(Boolean).forEach((key) => { if (!keys.includes(key)) keys.unshift(key); });
  return keys.map((key) => ({ value: key, label: monthLabel(key) }));
};

// Attachment rules (from the mockup): PDF, JPG or PNG up to 5 MB.
export const ATTACHMENT_TYPES = [".pdf", ".jpg", ".jpeg", ".png"];
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

export const emptyLoanForm = () => ({
  user_id: "",
  employee_code: "",
  record_type: "Loan",
  description: "",
  amount: "",
  start_month: currentMonth(),
  payroll_month: currentMonth(),
  months: "12",
  reason: "",
  attachment: null, // new File picked in the form
  attachment_url: "", // file already saved on the request
});

const monthKey = (value) => {
  const parsed = moment(value, ["YYYY-MM-DD", "YYYY-MM", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format(MONTH_KEY) : "";
};

export const recordToLoanForm = (record) => {
  const start = moment(record.repayment_date, "YYYY-MM-DD", true);
  return {
    description: record.description || "",
    payroll_month: monthKey(record.payroll_month) || monthKey(record.repayment_date) || currentMonth(),
    attachment: null,
    attachment_url: record.attachment_url || "",
    user_id: record.user_id,
    employee_code: record.employee_code,
    employee_name: record.employee_name,
    department_name: record.department,
    designation: record.designation,
    image_url: record.image_url,
    record_type: record.record_type,
    amount: String(record.disbursed_amount || ""),
    start_month: start.isValid() ? start.format(MONTH_KEY) : currentMonth(),
    months: String(record.instalment_count || 1),
    reason: record.reason || "",
  };
};

const isPositive = (value) => String(value ?? "").trim() !== "" && Number.isFinite(Number(value)) && Number(value) > 0;
const isWholeBetween = (value, min, max) => Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= max;

export const loanInstalment = (form) =>
  isPositive(form.amount) && isWholeBetween(form.months, 1, 60) ? round2(Number(form.amount) / Number(form.months)) : 0;

export const validateLoanForm = (form) => {
  const errors = {};
  if (!form.employee_code) errors.employee_code = "Select an employee.";
  else if (!form.user_id) errors.employee_code = "This employee can't be used: their user account wasn't found.";
  if (!isPositive(form.amount)) errors.amount = "Enter an amount greater than 0.";
  if (form.record_type === "Loan") {
    if (!form.start_month) errors.start_month = "Select the repayment start month.";
    if (!isWholeBetween(form.months, 1, 60)) errors.months = "Enter 1 to 60 months.";
  } else if (!form.payroll_month) {
    errors.payroll_month = "Select the payroll month.";
  }
  if ((form.description || "").length > TEXT_MAX) errors.description = `Keep it to ${TEXT_MAX} characters.`;
  if (!form.reason.trim()) errors.reason = "Enter a reason.";
  if (form.attachment) {
    const name = (form.attachment.name || "").toLowerCase();
    if (!ATTACHMENT_TYPES.some((ext) => name.endsWith(ext))) errors.attachment = "Upload a PDF, JPG or PNG file.";
    else if (form.attachment.size > ATTACHMENT_MAX_BYTES) errors.attachment = "The file must be 5 MB or smaller.";
  }
  return errors;
};

// ---- Add Migrated Loan (opening balance from before Cauver) ----
// The remaining balance of a loan given (and approved) before Cauver,
// recovered in equal monthly instalments. A custom schedule is shown as
// Coming Soon: advance/request/ only takes equal instalments.

export const PLAN = { equal: "equal", custom: "custom" };

export const emptyMigratedForm = () => ({
  user_id: "",
  employee_code: "",
  original_amount: "",
  repaid_before: "",
  issue_date: "",
  repayment_plan: PLAN.equal,
  remaining_instalments: "4",
  instalment_amount: "",
  start_month: currentMonth(),
  additional_info: "",
});

export const migratedOutstanding = (form) =>
  Math.max(round2((Number(form.original_amount) || 0) - (Number(form.repaid_before) || 0)), 0);

export const validateMigratedForm = (form) => {
  const errors = {};
  if (!form.employee_code) errors.employee_code = "Select an employee.";
  else if (!form.user_id) errors.employee_code = "This employee can't be used: their user account wasn't found.";
  if (!isPositive(form.original_amount)) errors.original_amount = "Enter an amount greater than 0.";
  const repaidText = String(form.repaid_before ?? "").trim();
  if (repaidText === "" || !Number.isFinite(Number(repaidText)) || Number(repaidText) < 0) {
    errors.repaid_before = "Enter 0 or more.";
  } else if (isPositive(form.original_amount) && Number(repaidText) > Number(form.original_amount)) {
    errors.repaid_before = "Can't be more than the original amount.";
  }
  if (!moment(form.issue_date, "YYYY-MM-DD", true).isValid()) errors.issue_date = "Enter the issue date.";
  else if (form.issue_date > moment().format("YYYY-MM-DD")) errors.issue_date = "The issue date can't be in the future.";

  if (errors.original_amount || errors.repaid_before) return errors;
  // The outstanding balance is the request amount, so something must be left.
  const outstanding = migratedOutstanding(form);
  if (outstanding <= 0) {
    errors.repaid_before = "Nothing is left to recover: the amount already repaid equals the original amount.";
    return errors;
  }

  if (!isWholeBetween(form.remaining_instalments, 1, 60)) errors.remaining_instalments = "Select the number of instalments.";
  if (!isPositive(form.instalment_amount)) errors.instalment_amount = "Enter an amount greater than 0.";
  else if (isWholeBetween(form.remaining_instalments, 1, 60)) {
    // Only a rounding difference (under 1 paisa per instalment) is allowed.
    const count = Number(form.remaining_instalments);
    if (Math.abs(Number(form.instalment_amount) * count - outstanding) >= 0.01 * count) {
      errors.instalment_amount = "Instalments × amount must equal the outstanding balance.";
    }
  }
  if (!form.start_month) errors.start_month = "Select the payroll month recovery starts in.";
  return errors;
};
